import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LoginDTO, RegisterDTO, UserDTO } from "../../shared/schemas";
import { apiFetch, setUserCache } from "./client";
import { syncFromServer } from "./sync";
import { syncWidgetSessionWithId } from "../widget-bridge";

const USER_KEY = ["user"] as const;

interface AuthResponse extends UserDTO {
  token: string;
  recoveryKey?: string;
  sessionId?: string;
}

export function useCurrentUser() {
  return useQuery<UserDTO>({
    queryKey: USER_KEY,
    queryFn: () => apiFetch<UserDTO>("/users/me"),
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: LoginDTO) => {
      const response = await apiFetch<AuthResponse>("/users/login", {
        method: "POST",
        body: JSON.stringify(data),
      });
      setUserCache(response.id, response.role);
      await syncFromServer();
      if (response.sessionId) {
        syncWidgetSessionWithId(response.sessionId);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RegisterDTO) => {
      const response = await apiFetch<AuthResponse>("/users/register", {
        method: "POST",
        body: JSON.stringify(data),
      });
      setUserCache(response.id, response.role);
      await syncFromServer();
      if (response.sessionId) {
        syncWidgetSessionWithId(response.sessionId);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useRecover() {
  return useMutation({
    mutationFn: async (data: { email: string; recoveryKey: string; newPassword: string }) => {
      const response = await apiFetch<AuthResponse>("/users/recover", {
        method: "POST",
        body: JSON.stringify(data),
      });
      setUserCache(response.id, response.role);
      return response;
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) =>
      apiFetch<UserDTO>("/users/me", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_KEY });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiFetch<{ success: boolean }>("/users/me/password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useRegenerateRecoveryKey() {
  return useMutation({
    mutationFn: (data: { currentPassword: string }) =>
      apiFetch<{ recoveryKey: string }>("/users/me/recovery-key", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>("/users/me", { method: "DELETE" }),
  });
}
